import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { useMutation, useQuery } from 'react-fetching-library';
import { conformToMask } from 'react-text-mask';
import { compare } from 'fast-json-patch';
import isEqual from 'lodash/isEqual';
import { makeStyles } from '@material-ui/core';
import ContentWrapper from 'components/Wrappers/ContentWrapper';
import RoundedButton from 'components/Shared/RoundedButton';
import UnsavedChangesGuard from 'components/Shared/UnsavedChangesGuard';
import showServerError from 'helpers/showError';
import { MASK } from 'components/Shared/FormattedPhone';
import Loader from 'components/Shared/Loader';
import { ACCOUNT_TABS, ACCOUNT_TYPES } from 'components/Accounts/constants';
import useMetadataUpdate from 'hooks/useMetadataUpdate';
import BackButton from 'components/Shared/BackButton';
import { useDispatch, useSelector } from 'react-redux';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
  setMetadata,
  getTransitionWithVariable,
  setAccountBackendErrors,
  clearAccountBackendErrors,
} from '../../../redux_store';
import useFormikDirtyReset from '../../../hooks/useFormikDirtyReset';
import { formattedAccountsValues } from '../helpers';
import AccountForm from './AccountForm/AccountForm';
import { checkPermission } from '../../../helpers/checkPermissions';
import permissions from '../../../config/permissions';
import InfoIcon from '@material-ui/icons/Info';
import { Box, Typography } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  contentContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
  },
  actionsContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    padding: theme.spacing(3, 6),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(2),
    boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2, 4),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2),
      gap: theme.spacing(1),
    },
  },
  actionButton: {
    textTransform: 'uppercase',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 100,
    height: 40,
    [theme.breakpoints.down('sm')]: {
      minWidth: 100,
      padding: theme.spacing(1, 2),
      fontSize: 13,
    },
  },
  backButton: {
    marginRight: 'auto',
  },
  readOnlyAlert: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#fff8e1',
    border: '1px solid #ffe0b2',
    borderRadius: 8,
    maxWidth: 400,
    '& .warningIcon': {
      color: '#f57c00',
      fontSize: 20,
    },
    '& .alertText': {
      color: '#e65100',
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: 1.4,
    },
  },
}));

const AccountEdit = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { accountUuid } = useParams();
  const { en: { btn, errors, notifications, forms, titles, unsavedChanges } } = useSelector((state) => state.i18n);
  const { backendErrors } = useSelector((state) => state.accountEditing);
  const [account, setAccount] = useState();
  const {
    user,
    metadata,
    metadata: { currentAccount, currentAccountPermissions },
  } = useSelector((state) => state.user);

  const isViewMode = useMemo(
    () => !checkPermission(
      currentAccountPermissions,
      permissions.MANAGE_CHILD_ACCOUNTS,
    ),
    [currentAccountPermissions],
  );

  const [isSuccess, setIsSuccess] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [activeTab, setActiveTab] = useState(ACCOUNT_TABS.BASIC_INFORMATION);

  // Use hook to reset dirty state after successful save
  const formikRef = useFormikDirtyReset(isSuccess);

  // Handle tab change from AccountForm
  const handleTabChange = (tabIndex) => {
    setActiveTab(tabIndex);
  };

  // Clear backend errors when component unmounts or user navigates away
  useEffect(() => {
    return () => {
      dispatch(clearAccountBackendErrors());
    };
  }, [dispatch]);

  const { query } = useMetadataUpdate(useQuery, [
    {
      method: 'GET',
      endpoint: `/accounts/${accountUuid || currentAccount.uuid}`,
    },
    false,
  ]);

  // Fetch surveys and resources data once when page loads
  const { payload: surveysPayload, loading: surveysLoading } = useQuery({
    method: 'GET',
    endpoint: '/surveys',
  });

  const { payload: resourcesPayload, loading: resourcesLoading } = useQuery({
    method: 'GET',
    endpoint: '/resources',
  });

  useEffect(() => {
    query().then(({ error, payload }) => {
      if (!error) {
        const data = { ...payload };
        if (data.ispConfiguration
          && data.ispConfiguration.services
          && data.ispConfiguration.services.length) {
          data.ispConfiguration.services = data.ispConfiguration.services
            .map(({ cptCode, price, initialPrice }) => ({
              cptCode: cptCode,
              price: Number(price).toFixed(2).toString(),
              initialPrice: initialPrice,
            }));
        }

        setAccount(data);
      }
    });
  }, [query]);

  const accountEditAction = (patches) => ({
    method: 'PATCH',
    endpoint: `/accounts/${accountUuid || currentAccount.uuid}`,
    body: patches,
  });
  const { loading, mutate } = useMetadataUpdate(useMutation, [
    accountEditAction,
  ]);

  const editAccount = async (values, { setSubmitting }) => {
    let accountCopy = JSON.parse(JSON.stringify(account));
    let formattedValues = formattedAccountsValues(JSON.parse(JSON.stringify(values)), false, accountCopy);
    delete formattedValues.otherAddresses; // will be patched manually
    delete accountCopy.otherAddresses;
    delete formattedValues.instantSurveys; // will be patched manually
    delete accountCopy.instantSurveys;

    if (formattedValues
      && formattedValues.accountPermissions
      && formattedValues.accountPermissions.disableOrders
      && accountCopy
      && accountCopy.accountPermissions
      && accountCopy.accountPermissions.hasOwnProperty('disableOrdersAutomations')) {
      formattedValues.accountPermissions.disableOrdersAutomations = accountCopy.accountPermissions.disableOrdersAutomations;
    }

    // When a feature toggle is OFF in the outgoing payload, clear its
    // matching allowed-list so the server resets the whitelist
    // alongside the disable flag. Local Formik state stays untouched
    // — `enableReinitialize` + the `setAccount(newPayload)` below
    // re-syncs the UI from the server response only after a
    // successful save, so a failed mutate leaves the form intact.
    if (formattedValues?.accountPermissions?.disableAssessments) {
      formattedValues.allowedSurveys = [];
    }
    if (formattedValues?.accountPermissions?.disableResourcesAll) {
      formattedValues.allowedResources = [];
    }

    // compare all the values except deleted ones
    const patches = compare(accountCopy, formattedValues);
    // create a patch for an array manually

    if (!isEqual(account.otherAddresses, values.otherAddresses)) {
      patches.push({
        op: 'replace',
        path: '/otherAddresses',
        value: values.otherAddresses,
      });
    }

    // create patches for instantSurveys manually - specific field changes
    if (!isEqual(account.instantSurveys, values.instantSurveys)) {
      const originalSurveys = account.instantSurveys || [];
      const newSurveys = values.instantSurveys || [];

      // Handle array length changes or new/deleted surveys
      if (originalSurveys.length !== newSurveys.length) {
        patches.push({
          op: 'replace',
          path: '/instantSurveys',
          value: values.instantSurveys,
        });
      } else {
        // Compare individual surveys for specific field changes
        originalSurveys.forEach((originalSurvey, index) => {
          const newSurvey = newSurveys[index];
          if (newSurvey && originalSurvey) {
            // Check each field for changes
            Object.keys(newSurvey).forEach(key => {
              if (!isEqual(originalSurvey[key], newSurvey[key])) {
                patches.push({
                  op: 'replace',
                  path: `/instantSurveys/${index}/${key}`,
                  value: newSurvey[key],
                });
              }
            });
          }
        });
      }
    }
    const wasBusinessNameChanged = !isEqual(
      account.businessName,
      values.businessName,
    );

    dispatch(clearAccountBackendErrors());

    const { payload, error, status } = await mutate(patches);

    if (error) {
      setIsFailed(true);
      setTimeout(() => {
        setIsFailed(false);
      }, 2000);
      const options = payload && {
        correlationUUID: payload.correlationUUID,
        userUUID: user.uuid,
      };

      // Handle modal submission errors
      if (window.currentModalSubmitHandler && window.currentModalSubmitHandler.onError) {
        if (status === 422 && payload.validationErrors) {
          window.currentModalSubmitHandler.onError({
            validationErrors: payload.validationErrors
          });
        } else {
          window.currentModalSubmitHandler.onError({
            message: 'Failed to save changes'
          });
        }
        window.currentModalSubmitHandler = null; // Clear the handler
      } else {
        // Original error handling for non-modal submissions
        switch (status) {
          case 422: {
            showServerError(dispatch, errors.validationError);
            dispatch(setAccountBackendErrors(payload.validationErrors));
            break;
          }
          default: {
            showServerError(dispatch, '', options);
            break;
          }
        }
      }
    } else if (payload) {
      // Handle successful modal submission
      if (window.currentModalSubmitHandler && window.currentModalSubmitHandler.onSuccess) {
        window.currentModalSubmitHandler.onSuccess();
        window.currentModalSubmitHandler = null; // Clear the handler
      }

      if (wasBusinessNameChanged) {
        const newAccounts = [...metadata.accounts].map((account) => {
          if (account.uuid === (accountUuid || currentAccount.uuid)) {
            return {
              ...account,
              businessName: values.businessName,
            };
          }
          return account;
        });
        dispatch(
          setMetadata({
            ...metadata,
            accounts: newAccounts,
          }),
        );
      }
      setTimeout(() => {
        setIsSuccess(false);
      }, 2000);

      let newEmailConfiguration = {};
      let newSmsConfiguration = {};

      Object.keys(payload.emailConfiguration).forEach(function(key) {
        if (payload.emailConfiguration[key] && payload.emailConfiguration[key].length > 0) {
          newEmailConfiguration[key] = payload.emailConfiguration[key];
        } else if (account.emailConfiguration[key] && account.emailConfiguration[key].length > 0) {
          newEmailConfiguration[key] = account.emailConfiguration[key];
        } else {
          newEmailConfiguration[key] = [];
        }
      });
      Object.keys(payload.smsConfiguration).forEach(function(key) {
        if (payload.smsConfiguration[key] && payload.smsConfiguration[key].length > 0) {
          newSmsConfiguration[key] = payload.smsConfiguration[key];
        } else if (account.smsConfiguration[key] && account.smsConfiguration[key].length > 0) {
          newSmsConfiguration[key] = account.smsConfiguration[key];
        } else {
          newSmsConfiguration[key] = [];
        }
      });

      const newPayload = {
        ...payload,
        emailConfiguration: newEmailConfiguration,
        smsConfiguration: newSmsConfiguration,
      };

      setAccount(newPayload);
      dispatch(
        enqueueSnackbar({
          message: getTransitionWithVariable(notifications.accountEdited, {
            name: formattedValues.businessName,
          }),
          options: {
            variant: SNACKBAR_VARIANTS.success,
          },
        }),
      );

      setIsSuccess(true);
    }
    setSubmitting(false);
  };

  const checkIsDefault = (firstKey, secondKey) => {
    return account?.[firstKey]?.[secondKey]?.find((e) => e.local === 'english')?.isDefault ||
      account?.[firstKey]?.[secondKey]?.find((e) => e.local === 'spanish')?.isDefault ||
      account?.[firstKey]?.[secondKey].length === 0;
  };

  const findEmailOrSmsText = (firstKey, secondKey, local) => {
    return account?.[firstKey]?.[secondKey]?.find(
      (e) => e.local === local,
    )?.text;
  };

  return account ? (
    <Formik
      innerRef={formikRef}
      initialValues={{
        ...account,
        phone: conformToMask(account.phone, MASK).conformedValue,
        fax: conformToMask(account.fax, MASK).conformedValue,
        ianaTimeZone: account.ianaTimeZone || 'America/New_York',
        isShippingTheSame: isEqual(
          account.organizationAddress,
          account.shippingAddress,
        ),
        isDistributionSponsor:
          account.accountType === ACCOUNT_TYPES.DISTRIBUTION_SPONSOR,
        accountPermissions: account.accountPermissions,
        allowedSurveys: account.allowedSurveys || [],
        allowedResources: (account.allowedResources || []).filter(elem => !elem.endsWith('_spanish')),
        customTags: account.customTags,

        emailSurveyInvitationEnglish: findEmailOrSmsText('emailConfiguration', 'surveyInvitation', 'english'),
        emailSurveyInvitationSpanish: findEmailOrSmsText('emailConfiguration', 'surveyInvitation', 'spanish'),
        emailSurveyInvitationIsDefault: checkIsDefault('emailConfiguration', 'surveyInvitation'),
        emailResourceInvitationEnglish: findEmailOrSmsText('emailConfiguration', 'resourceInvitation', 'english'),
        emailResourceInvitationSpanish: findEmailOrSmsText('emailConfiguration', 'resourceInvitation', 'spanish'),
        emailResourceInvitationIsDefault: checkIsDefault('emailConfiguration', 'resourceInvitation'),
        emailSurveyReminderEnglish: findEmailOrSmsText('emailConfiguration', 'surveyReminder', 'english'),
        emailSurveyReminderSpanish: findEmailOrSmsText('emailConfiguration', 'surveyReminder', 'spanish'),
        emailSurveyReminderIsDefault: checkIsDefault('emailConfiguration', 'surveyReminder'),
        emailResourceReminderEnglish: findEmailOrSmsText('emailConfiguration', 'resourceReminder', 'english'),
        emailResourceReminderSpanish: findEmailOrSmsText('emailConfiguration', 'resourceReminder', 'spanish'),
        emailResourceReminderIsDefault: checkIsDefault('emailConfiguration', 'resourceReminder'),
        emailSurveyCompletionEnglish: findEmailOrSmsText('emailConfiguration', 'surveyCompletion', 'english'),
        emailSurveyCompletionSpanish: findEmailOrSmsText('emailConfiguration', 'surveyCompletion', 'spanish'),
        emailSurveyCompletionIsDefault: checkIsDefault('emailConfiguration', 'surveyCompletion'),

        smsSurveyInvitationEnglish: findEmailOrSmsText('smsConfiguration', 'surveyInvitation', 'english'),
        smsSurveyInvitationSpanish: findEmailOrSmsText('smsConfiguration', 'surveyInvitation', 'spanish'),
        smsSurveyInvitationIsDefault: checkIsDefault('smsConfiguration', 'surveyInvitation'),
        smsResourceInvitationEnglish: findEmailOrSmsText('smsConfiguration', 'resourceInvitation', 'english'),
        smsResourceInvitationSpanish: findEmailOrSmsText('smsConfiguration', 'resourceInvitation', 'spanish'),
        smsResourceInvitationIsDefault: checkIsDefault('smsConfiguration', 'resourceInvitation'),
        smsSurveyReminderEnglish: findEmailOrSmsText('smsConfiguration', 'surveyReminder', 'english'),
        smsSurveyReminderSpanish: findEmailOrSmsText('smsConfiguration', 'surveyReminder', 'spanish'),
        smsSurveyReminderIsDefault: checkIsDefault('smsConfiguration', 'surveyReminder'),
        smsResourceReminderEnglish: findEmailOrSmsText('smsConfiguration', 'resourceReminder', 'english'),
        smsResourceReminderSpanish: findEmailOrSmsText('smsConfiguration', 'resourceReminder', 'spanish'),
        smsResourceReminderIsDefault: checkIsDefault('smsConfiguration', 'resourceReminder'),
        smsSurveyCompletionEnglish: findEmailOrSmsText('smsConfiguration', 'surveyCompletion', 'english'),
        smsSurveyCompletionSpanish: findEmailOrSmsText('smsConfiguration', 'surveyCompletion', 'spanish'),
        smsSurveyCompletionIsDefault: checkIsDefault('smsConfiguration', 'surveyCompletion'),
        loading: forms.loading,
      }}
      validateOnBlur={false}
      validateOnChange={false}
      validateOnMount={false}
      enableReinitialize
      onSubmit={editAccount}
    >
      {({
          values,
          handleSubmit,
          dirty,
          setFieldValue,
          setSubmitting,
        }) => (
        <UnsavedChangesGuard
          hasUnsavedChanges={!isViewMode && dirty}
          title={unsavedChanges.title}
          message={getTransitionWithVariable(unsavedChanges.accountEdit, {
            name: account.businessName || 'account'
          })}
          discardButtonText={unsavedChanges.leaveAnyway}
          keepEditingButtonText={unsavedChanges.keepEditing}
        >
          <ContentWrapper
            titleText={titles.editAccount}
            actions={isViewMode && (
              <Box className={classes.readOnlyAlert}>
                <InfoIcon className="warningIcon" />
                <Typography className="alertText">
                  {titles.readOnlyNoPermissions}
                </Typography>
              </Box>
            )}
            containerStyle={{
              overflow: 'visible',
              maxHeight: 'calc(100% - 35px)',
              boxSizing: 'border-box',
              paddingBottom: 0,
            }}
          >
            <div className={classes.contentContainer}>
            <AccountForm
              values={values}
              backendErrors={{ ...backendErrors }}
              isEditMode
              isViewMode={isViewMode}
              accountUuid={accountUuid || currentAccount.uuid}
              setBackendErrors={(errors) => dispatch(setAccountBackendErrors(errors))}
              setAccount={setAccount}
              sectionClassName={classes.section}
              sectionTitleClassName={classes.sectionTitle}
              surveysPayload={surveysPayload}
              resourcesPayload={resourcesPayload}
              surveysLoading={surveysLoading}
              resourcesLoading={resourcesLoading}
              onTabChange={handleTabChange}
              setFieldValue={setFieldValue}
              setSubmitting={setSubmitting}
              editAccount={editAccount}
              dirty={dirty}
              formikRef={formikRef}
            />
            {/* Hide footer when Instant Assessments tab (index 4) is active */}
            {activeTab !== ACCOUNT_TABS.INSTANT_ASSESSMENTS && (
              <div className={classes.actionsContainer}>
                <BackButton className={classes.backButton} />
                {!isViewMode && (
                  <RoundedButton
                  type='submit'
                  variant='contained'
                  color='primary'
                  disabled={
                    loading ||
                    values.scheduleDeliverTime === 'invalid_value'
                  }
                  onClick={handleSubmit}
                  isLoading={loading}
                  isSuccess={isSuccess}
                  isFailed={isFailed}
                  className={classes.actionButton}
                >
                  {btn.save}
                </RoundedButton>
                )}
              </div>
            )}
          </div>
        </ContentWrapper>
        </UnsavedChangesGuard>
      )}
    </Formik>
  ) : (
    <ContentWrapper>
      <Loader fullHeight />
    </ContentWrapper>
  );
};

export default AccountEdit;
